import React, { useEffect, useMemo, useState } from 'react';
import {
  Row, Col, Card, Form, Table, Badge, 
  Button, Toast, ToastContainer, Modal, Spinner, Pagination, Accordion
} from 'react-bootstrap';
import { FaCheck, FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from "../../contexts/AuthContext";

// ====== Backend Endpoints ======

const defaultForm = {
  type: 'Right to Access',
  priority: 'Standard (30 days)',
  email: '',
  systems: [],
  otherSystem: '', // New field for custom system input
  details: '',
};
 
// ====== Categorized Systems List ======
const SYSTEMS_CATEGORIES = {
  'Core Business & IT Systems': [
    'Customer Support / Ticketing Systems',
    'ERP Systems',
    'Order Management Systems',
    'Vendor Management Systems',
    'Partner Portals',
    'Franchise or Distributor Systems'
  ],
  'Identity, Access & Security': [
    'Identity and Access Management (IAM)',
    'Single Sign-On (SSO) Systems',
    'Authentication Systems (OTP, MFA, Password Vaults)',
    'Privileged Access Management (PAM)',
    'Device Management (MDM, Endpoint Management)'
  ],
  'Communication & Collaboration': [
    'Email Systems (Microsoft 365, Google Workspace)',
    'Messaging Platforms (WhatsApp Business, SMS Gateways)',
    'Call Center / IVR Systems',
    'Video Conferencing Tools',
    'Internal Collaboration Tools (Chat, Intranet)'
  ],
  'Digital Platforms & Channels': [
    'Websites and Web Portals',
    'Mobile Applications',
    'Customer Dashboards',
    'Partner Dashboards',
    'Self-Service Portals'
  ],
  'Data Storage & Processing': [
    'Databases (SQL, NoSQL)',
    'Data Warehouses',
    'Data Lakes',
    'Cloud Storage (Object Storage, File Storage)',
    'Backup and Archival Systems'
  ],
  'Marketing & Tracking Technologies': [
    'Consent Management Platforms',
    'Cookie Management Tools',
    'AdTech Platforms',
    'Social Media Integrations',
    'Lead Management Systems'
  ],
  'Finance & Compliance': [
    'Accounting Systems',
    'Payroll Systems',
    'Tax and Statutory Filing Systems',
    'KYC / AML Systems',
    'Fraud Detection Systems'
  ],
  'Third-Party & External Integrations': [
    'Payment Gateways',
    'Logistics and Delivery Platforms',
    'Government Portals',
    'Regulatory Reporting Systems',
    'Outsourced Processing Vendors'
  ],
  'Monitoring, Logs & Risk Systems': [
    'Log Management Systems',
    'SIEM / SOC Platforms',
    'Audit Trail Systems',
    'Incident Management Tools',
    'Risk and Compliance Tools'
  ],
  'Industry-Specific': [
    'Healthcare Systems (EMR, HIS)',
    'BFSI Core Banking / Insurance Systems',
    'Telecom Subscriber Management Systems',
    'EdTech Student Information Systems'
  ]
};

function useForceUpdate() {
  const [, setTick] = useState(0);
  return () => setTick(t => t + 1);
}

const DsarManagementTab = ({ userData, onScoreUpdate,logToAccessLogs }) => {
  const forceUpdate = useForceUpdate();
  const { profile } = useAuth();
   const organizationId = profile?.orgId;
  const userId =organizationId;

  // ---- table + filters state ----
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [filters, setFilters] = useState({
    status: '',
    type: '',
    dueDate: '',
  });

  // ---- UI state ----
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  // ---- modal state ----
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);

  // ========== HELPERS ==========
  const getUserRegion = () => sessionStorage.getItem('userRegion') || 'GB';

  const logActivity = async (message, badge = "info") => {
    try {
     await axios.post(
  "https://ugat9qq1mj.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpdp/activities/create",
  {
    message,
    badge,
    timestamp: new Date().toISOString()
  },
  {
    headers: {
      Region: getUserRegion(),
      organizationId: organizationId
    }
  }
);
    } catch (err) {
      console.warn("Failed to log activity:", err.message);
    }
  };
 

  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 3000);
  };

  const resetForm = () => {
    setForm(defaultForm);
    setEditing(null);
  };

  // ========== API INTEGRATION ==========
  // const fetchAllDsars = async () => {
  //   const headers = {
  //     Region: getUserRegion(),
  //     organizationId: organizationId,
  //   };
  //   try {
  //     setLoading(true);
  //     const { data } = await axios.get(
  //       `https://037wtgmzxe.execute-api.ap-southeast-2.amazonaws.com/prod/api/dsar/by-user`,
  //       { headers }
  //     );

  //     let filtered = data || [];
  //     if (filters.status) filtered = filtered.filter(r => r.status === filters.status);
  //     if (filters.type) filtered = filtered.filter(r => r.type === filters.type);
  //     if (filters.dueDate) filtered = filtered.filter(r => r.dueDate === filters.dueDate);

  //     setRequests(filtered.slice((page - 1) * pageSize, page * pageSize));
  //     setTotal(filtered.length);
  //   } catch (e) {
  //     showToast(e.message || 'Failed to load DSARs', 'danger');
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  /***cross region fetchAllDsars */
  // Replace your existing fetchAllDsars function with this:
const fetchAllDsars = async () => {
  try {
    setLoading(true);
    const { data } = await axios.get(
      `https://037wtgmzxe.execute-api.ap-southeast-2.amazonaws.com/prod/api/dsar/by-user`,
      {
        headers: {
          Region: getUserRegion(),       // still sent â€” backend uses as primary hint
          organizationId: organizationId,
        }
      }
    );

    let filtered = data || [];
    if (filters.status) filtered = filtered.filter(r => r.status === filters.status);
    if (filters.type)   filtered = filtered.filter(r => r.type === filters.type);
    if (filters.dueDate) filtered = filtered.filter(r => r.dueDate === filters.dueDate);

    setTotal(filtered.length);
    // âœ… Paginate AFTER filtering the full cross-region merged list
    setRequests(filtered.slice((page - 1) * pageSize, page * pageSize));

  } catch (e) {
    showToast(e.message || 'Failed to load DSARs', 'danger');
  } finally {
    setLoading(false);
  }
};

  const createDsar = async (payload) => {
    const headers = {
      Region: getUserRegion(),
      organizationId: organizationId,
    };
    return axios.post(
      `https://upkr0d8p84.execute-api.ap-southeast-2.amazonaws.com/prod/api/dsar/create`,
      payload,
      { headers }
    );
  };

  const updateDsar = async (id, payload) => {
    const headers = {
      Region: getUserRegion(),
      organizationId: organizationId,
    };
    payload.requestId = id;
    return axios.put(
      `https://8ftarq96lj.execute-api.ap-southeast-2.amazonaws.com/prod/api/dsar/update`,
      payload,
      { headers }
    );
  };

  const deleteDsar = async (id) => {
    const headers = {
      Region: getUserRegion(),
      organizationId: organizationId,
    };
    return axios.delete(
      `https://vas48jbeea.execute-api.ap-southeast-2.amazonaws.com/prod/api/dsar/delete`,
      {
        headers,
        params: { requestId: id },
      }
    );
  };

  // ========== EFFECTS ==========
  useEffect(() => {
    fetchAllDsars();
  }, [page, filters.status, filters.type, filters.dueDate]);

  // ========== STATS ==========
  const stats = useMemo(() => {
    const allRequests = requests;
    const total = allRequests.length;
    const pending = allRequests.filter(r => r.status === 'Pending').length;
    const completed = allRequests.filter(r => r.status === 'Completed').length;
    const overdue = allRequests.filter(
      r => r.dueDate && new Date(r.dueDate) < new Date() && r.status !== 'Completed'
    ).length;
    return { total, pending, completed, overdue };
  }, [requests]);

  // ========== HANDLERS ==========
  const handleFilterChange = (e) => {
    setPage(1);
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSystemToggle = (system) => {
    setForm(prev => ({
      ...prev,
      systems: prev.systems.includes(system)
        ? prev.systems.filter(s => s !== system)
        : [...prev.systems, system],
    }));
  };

  const onCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const onEdit = (row) => {
    setEditing(row);
    
    // Separate "Other:" prefixed systems from standard ones
    const standardSystems = [];
    let otherSystemValue = '';
    
    (row.platforms || []).forEach(platform => {
      if (platform.startsWith('Other: ')) {
        otherSystemValue = platform.replace('Other: ', '');
      } else {
        standardSystems.push(platform);
      }
    });
    
    setForm({
      type: mapTypeToForm(row.type),
      priority: mapPriorityToForm(row.priority),
      email: row.requestor,
      systems: standardSystems,
      otherSystem: otherSystemValue,
      details: row.details || '',
    });
    setShowModal(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this DPAR?')) return;
    try {
      await deleteDsar(id);
      showToast('Deleted');
      const deleted = requests.find(r => r.requestId === id);
      await logActivity(`Deleted DPAR request for ${deleted?.requestor || 'unknown'}`, "danger");
      logToAccessLogs?.({
  action:      'DPAR Deleted',
  severity:    'critical',
  description: `Deleted DPAR request for ${deleted?.requestor || 'unknown'} (${deleted?.type})`,});
      fetchAllDsars();
    } catch (e) {
      showToast(e.message || 'Delete failed', 'danger');
    }
  };

  const onMarkComplete = async (row) => {
    try {
      await updateDsar(row.requestId, { status: 'Completed' });
      showToast('Marked completed');
      await logActivity(`Marked DPAR request for ${row.requestor} as completed`, "info");
      logToAccessLogs?.({
  action:      'DPAR Completed',
  severity:    'success',
  description: `Marked DPAR (${row.type}) for ${row.requestor} as completed`,});
      fetchAllDsars();
    } catch (e) {
      showToast(e.message || 'Failed to update', 'danger');
    }
  };

  // ========== MAPPERS ==========
  function mapFormToPayload(f) {
    const dueDays = {
      'Standard (30 days)': 30,
      'High (15 days)': 15,
      'Critical (7 days)': 7,
    }[f.priority];
    
    // Combine standard systems with "Other" if provided
    const allSystems = [...f.systems];
    if (f.otherSystem && f.otherSystem.trim()) {
      allSystems.push(`Other: ${f.otherSystem.trim()}`);
    }
    
    return {
      requestor: f.email,
      platforms: allSystems,
      type: normalizeTypeForApi(f.type),
      priority: normalizePriorityForApi(f.priority),
      status: 'Pending',
      dueDate: new Date(Date.now() + dueDays * 86400000).toISOString().split('T')[0],
      details: f.details,
    };
  }

  function normalizeTypeForApi(type) {
    switch (type) {
      case 'Right to Access': return 'Access';
      case 'Right to Correction': return 'Correction';
      case 'Right to Erasure': return 'Deletion';
      case 'Right to Data Portability': return 'Portability';
      default: return type;
    }
  }

  function normalizePriorityForApi(priority) {
    switch (priority) {
      case 'Critical (7 days)': return 'High';
      case 'High (15 days)': return 'Medium';
      default: return 'Low';
    }
  }

  function mapTypeToForm(apiType) {
    switch (apiType) {
      case 'Access': return 'Right to Access';
      case 'Correction': return 'Right to Correction';
      case 'Deletion': return 'Right to Erasure';
      case 'Portability': return 'Right to Data Portability';
      default: return 'Right to Access';
    }
  }

  function mapPriorityToForm(apiPriority) {
    switch (apiPriority) {
      case 'High': return 'Critical (7 days)';
      case 'Medium': return 'High (15 days)';
      default: return 'Standard (30 days)';
    }
  }

  // ========== SUBMIT + HELPERS ==========
  const handleSubmit = async () => {
    const payload = mapFormToPayload(form);
    try {
      setSaving(true);
      if (editing) {
        await updateDsar(editing.requestId, payload);
        showToast('DPAR updated');
        await logActivity(`Updated DPAR request for ${form.email}`, "warning");
        
        logToAccessLogs?.({
  action:      'DPAR Updated',
  severity:    'warning',
  description: `Updated DPAR request (${form.type}) for ${form.email}`,});
      
      } else {
        await createDsar(payload);
        showToast('DPAR created');
        await logActivity(`Created DPAR request for ${form.email}`, "success");
        logToAccessLogs?.({
  action:      'DPAR Created',
  severity:    'success',
  description: `Created DPAR request (${form.type}) for ${form.email}`,});
      }

      setShowModal(false);
      resetForm();
      fetchAllDsars();
    } catch (e) {
      showToast(e.message || 'Save failed', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const pageCount = Math.ceil(total / pageSize);

  // ========== RENDER ==========

  useEffect(() => {
    if (!loading && onScoreUpdate) {
      const SCORE_COMPLETED = 100;
      const OVERDUE_PENALTY = 30;
      const PENDING_PENALTY = 15;
      const NO_DATA_BASE = 0;

      const { total, completed, pending, overdue } = stats;

      let score = 0;

      if (total === 0) {
        score = NO_DATA_BASE;
      } else {
        score = SCORE_COMPLETED;
        score -= pending * PENDING_PENALTY;
        score -= overdue * OVERDUE_PENALTY;
        score = Math.max(0, Math.min(SCORE_COMPLETED, score));
      }

      onScoreUpdate(Math.round(score));
    }
  }, [stats, loading, onScoreUpdate]);

  return (
    <>
      {/* Table + Filters */}
      <Row className="mb-3">
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Data Principal Access Requests</h5>
              <Button variant="success" className='NewTransfer' size="sm" onClick={onCreate}>
                <FaPlus className="me-1" /> New DPAR
              </Button>
            </Card.Header>
            <Card.Body>
              {/* Filters */}
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Select name="status" value={filters.status} onChange={handleFilterChange}>
                    <option value="">All Requests</option>
                    <option>Pending</option>
                    <option>In Progress</option>
                    <option>Completed</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Select name="type" value={filters.type} onChange={handleFilterChange}>
                    <option value="">All Types</option>
                    <option>Access</option>
                    <option>Correction</option>
                    <option>Deletion</option>
                    <option>Portability</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <Form.Control
                    type="date"
                    name="dueDate"
                    value={filters.dueDate}
                    onChange={handleFilterChange}
                  />
                </Col>
                <Col md={3} className="text-end">
                  <Button variant="primary" onClick={fetchAllDsars}>Apply</Button>
                </Col>
              </Row>

              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : (
                <>
                  <Table striped bordered hover responsive>
                    <thead className="table-dark">
                      <tr>
                        <th>Request ID</th>
                        <th>Type</th>
                        <th>Requestor</th>
                        <th>Platforms</th>
                        <th>Priority</th>
                        <th>Status</th>
                        <th>Due Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.length ? (
                        requests.map(r => (
                          <tr key={r.requestId}>
                            <td>{r.requestId}</td>
                            <td><Badge bg="primary">{r.type}</Badge></td>
                            <td>{r.requestor}</td>
                            <td>
                              {r.platforms?.map((p, idx) => (
                                <Badge key={idx} bg="secondary" className="me-1">{p}</Badge>
                              ))}
                            </td>
                            <td>
                              <Badge
                                bg={r.priority === 'High' ? 'danger' : r.priority === 'Medium' ? 'warning' : 'info'}
                              >
                                {r.priority}
                              </Badge>
                            </td>
                            <td>
                              <Badge
                                bg={r.status === 'Completed' ? 'success' : r.status === 'Pending' ? 'warning' : 'secondary'}
                              >
                                {r.status}
                              </Badge>
                            </td>
                            <td>{r.dueDate}</td>
                            <td>
                              <Button
                                variant="outline-success"
                                size="sm"
                                className="me-1"
                                onClick={() => onMarkComplete(r)}
                                disabled={r.status === 'Completed'}
                              >
                                <FaCheck />
                              </Button>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-1"
                                onClick={() => onEdit(r)}
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => onDelete(r.requestId)}
                              >
                                <FaTrash />
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className="text-center text-muted py-4">
                            No DPARs found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>

                  {/* Pagination */}
                  {pageCount > 1 && (
                    <div className="d-flex justify-content-end">
                      <Pagination className="mb-0">
                        <Pagination.Prev
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          disabled={page === 1}
                        />
                        {[...Array(pageCount)].map((_, i) => (
                          <Pagination.Item
                            key={i + 1}
                            active={page === i + 1}
                            onClick={() => setPage(i + 1)}
                          >
                            {i + 1}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next
                          onClick={() => setPage(p => Math.min(pageCount, p + 1))}
                          disabled={page === pageCount}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Stats */}
      <Row>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-primary">{stats.total}</h3>
              <p className="mb-0">Total Requests</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-warning">{stats.pending}</h3>
              <p className="mb-0">Pending</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-success">{stats.completed}</h3>
              <p className="mb-0">Completed</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-danger">{stats.overdue}</h3>
              <p className="mb-0">Overdue</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Toast */}
      <ToastContainer position="bottom-end" className="p-3">
        <Toast show={toast.show} bg={toast.variant}>
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered backdrop="static" size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Edit DPAR' : 'Create DPAR'}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Request Type</Form.Label>
                  <Form.Select
                    value={form.type}
                    onChange={e => setForm({ ...form, type: e.target.value })}
                  >
                    <option>Right to Access</option>
                    <option>Right to Correction</option>
                    <option>Right to Erasure</option>
                    <option>Right to Data Portability</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority Level</Form.Label>
                  <Form.Select
                    value={form.priority}
                    onChange={e => setForm({ ...form, priority: e.target.value })}
                  >
                    <option>Standard (30 days)</option>
                    <option>High (15 days)</option>
                    <option>Critical (7 days)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Requestor Email</Form.Label>
              <Form.Control
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
              />
            </Form.Group>
            
            {/* Affected Systems with Accordion */}
            <Form.Group className="mb-3">
              <Form.Label>Affected Systems ({form.systems.length} selected)</Form.Label>
              <Accordion>
                {Object.entries(SYSTEMS_CATEGORIES).map(([category, systems], categoryIdx) => (
                  <Accordion.Item eventKey={String(categoryIdx)} key={category}>
                    <Accordion.Header>{category}</Accordion.Header>
                    <Accordion.Body>
                      {systems.map(system => (
                        <Form.Check
                          key={system}
                          type="checkbox"
                          label={system}
                          checked={form.systems.includes(system)}
                          onChange={() => handleSystemToggle(system)}
                          className="mb-1"
                        />
                      ))}
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
              
              {/* Other System Input - Shows when user needs custom input */}
              <div className="mt-3">
                <Form.Label>Other System (specify if not listed above)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter custom system name"
                  value={form.otherSystem}
                  onChange={e => setForm({ ...form, otherSystem: e.target.value })}
                />
                <Form.Text className="text-muted">
                  Use this field to specify any system not listed in the categories above
                </Form.Text>
              </div>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Additional Details</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.details}
                onChange={e => setForm({ ...form, details: e.target.value })}
                placeholder="Any specific requirements or context"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={saving} variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button disabled={saving} variant="primary" onClick={handleSubmit}>
            {saving && <Spinner size="sm" className="me-2" animation="border" />}
            {editing ? 'Save' : 'Create DPAR'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DsarManagementTab;